import Header from '../../components/Header/Header';
import { useEffect, useState, useMemo } from 'react';
import { useBackendFilter } from '../../hooks/usePosts';
import TableContainer from '../../components/TableContainer/TableContainer';
import { IMedData, IMedicalCheck, IMedList } from '../../types/type';
import MedicList from '../../components/MedicList/MedicList';
import StatusButtons from '../../components/StatusButtons/StatusButtons';
import MedicDetails from '../../components/MedicDetails/MedicDetails';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import './medic.css';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setMedicLists } from '../../store/meddata/meddataSlice';
import Pagination from '../../components/Pagination/Pagination';
import Tabs from '../../components/Tabs/Tabs';
import { toBackendDate } from '../../utils/utils';
import { CHECK_STATUS_FINAL_MESSAGE, isCheckStatusFinal } from '../../utils/checkStatusErrors';
import { MedicalService } from '../../services/medical.service';

// Компонент для новой вкладки "Заявки на МО"
function MedicalRequestsTab({ user, onUserClick }: { user: any; onUserClick: (id: string) => void }) {
  const dispatch = useAppDispatch();

  // Основной фильтр для Header
  const [filter, setFilter] = useState<{
    sort: string;
    query: string;
    filterQuery: string;
  }>({
    sort: '',
    query: '',
    filterQuery: 'fullName',
  });

  const [medStatus, setMedStatus] = useState('waiting');

  // Дополнительные фильтры
  const [extendedFilters, setExtendedFilters] = useState({
    startTime: '',
    endTime: '',
    autoparkName: '',
    num: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Используем хук useBackendFilter для получения данных с нового эндпоинта
  const { data: medLists, loading, error, totalCount, totalPages, fetchData } = useBackendFilter('/medlist');

  // Сохраняем данные в Redux при их получении
  useEffect(() => {
    if (medLists && !loading) {
      dispatch(
        setMedicLists({
          data: medLists,
          totalCount,
          totalPages,
        }),
      );
    }
  }, [medLists, loading, totalCount, totalPages, dispatch]);

  // Подсчет статистики медицинских осмотров
  const medStats = useMemo(() => {
    const lists = (medLists as IMedicalCheck[]) || [];
    const totalCount = lists.length || 0;
    const positiveCount = lists.filter((med) => med.status === 'completed').length || 0;
    const negativeCount = lists.filter((med) => med.status === 'rejected').length || 0;

    return {
      totalCount,
      positiveCount,
      negativeCount,
    };
  }, [medLists]);

  // Функция для сброса фильтров
  const resetFilters = () => {
    setFilter({
      sort: '',
      query: '',
      filterQuery: 'fullName',
    });
    setExtendedFilters({
      startTime: '',
      endTime: '',
      autoparkName: '',
      num: '',
    });
    setMedStatus('waiting');
    setCurrentPage(1);
  };

  // Обработчик изменения расширенных фильтров
  const handleExtendedFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setExtendedFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Функция для получения данных с сервера с применением фильтров
  const applyFilters = () => {
    const filterParams: Record<string, any> = {
      page: currentPage,
      limit: itemsPerPage,
    };

    // Добавляем autoparkIDs из профиля пользователя
    if (user.autoparkIDs && user.autoparkIDs.length > 0) {
      filterParams.autoparkIDs = user.autoparkIDs.join(',');
    }

    // Добавляем поисковый запрос и поле поиска, если они есть
    if (filter.query && filter.filterQuery) {
      if (filter.filterQuery === 'name' || filter.filterQuery === 'middleName' || filter.filterQuery === 'firstName') {
        filterParams.firstName = filter.query;
      } else if (filter.filterQuery === 'lastName') {
        filterParams.lastName = filter.query;
      } else if (filter.filterQuery === 'fullName') {
        filterParams.fullName = filter.query;
      } else {
        filterParams[filter.filterQuery] = filter.query;
      }

      filterParams.query = filter.query;
      filterParams.filterQuery = filter.filterQuery;
    }

    // Добавляем статус медосмотра, если он выбран
    if (medStatus) {
      filterParams.status = medStatus;
    }

    // Добавляем сортировку, если она выбрана
    if (filter.sort) {
      filterParams.sort = filter.sort;
    }

    // Добавляем период дат, если они указаны, конвертируя в нужный формат
    if (extendedFilters.startTime) {
      filterParams.startTime = toBackendDate(extendedFilters.startTime);
    }
    if (extendedFilters.endTime) {
      filterParams.endTime = toBackendDate(extendedFilters.endTime);
    }

    // Добавляем автопарк, если он указан
    if (extendedFilters.autoparkName) {
      filterParams.autoparkName = extendedFilters.autoparkName;
    }

    // Добавляем номер заявки, если он указан
    if (extendedFilters.num) {
      filterParams.num = extendedFilters.num;
    }

    fetchData(filterParams);
  };

  // Первоначальная загрузка данных
  useEffect(() => {
    applyFilters();
  }, [user.autoparkIDs]);

  // Обновление данных при изменении фильтров или пагинации
  useEffect(() => {
    const handler = setTimeout(() => {
      applyFilters();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [
    filter.query,
    filter.filterQuery,
    filter.sort,
    extendedFilters.startTime,
    extendedFilters.endTime,
    extendedFilters.autoparkName,
    extendedFilters.num,
    medStatus,
    currentPage,
    itemsPerPage,
  ]);

  const handleStatusChange = (newValue: string) => {
    if (newValue !== medStatus) {
      setMedStatus(newValue);
      setCurrentPage(1);
    } else {
      setMedStatus('waiting');
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Функция для изменения статуса заявки на inProgress при открытии карточки
  const handleViewClick = async (id: string) => {
    try {
      // Получаем данные заявки для проверки текущего статуса
      const medList = (medLists as IMedicalCheck[])?.find((item) => item.id === id);

      // Если статус waiting, меняем на inProgress при открытии карточки
      if (medList && medList.status === 'waiting') {
        await MedicalService.updateMedListStatus({
          id,
          status: 'inProgress',
          medicID: user.id,
        });

        // Обновляем данные после изменения статуса
        applyFilters();
      }
    } catch (error) {
      if (isCheckStatusFinal(error)) {
        alert(CHECK_STATUS_FINAL_MESSAGE);
        applyFilters();
      } else {
        console.error('Ошибка при изменении статуса заявки:', error);
      }
    }

    // Открываем карточку заявки
    onUserClick(id);
  };

  const params = [
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'lastName',
      i_label: 'Фамилия',
    },
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'lastName',
      i_label: 'Имя',
    },
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'middleName',
      i_label: 'Отчество',
    },
  ];

  return (
    <>
      <Header filter={filter} setFilter={setFilter} params={params} />
      <div className="admin__container">
        <header className="admin__header">
          <div className="admin__header-container">
            <div className="medStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
              Осмотров: {medStats.totalCount}, из них Положительных {medStats.positiveCount}, Отрицательных{' '}
              {medStats.negativeCount}
            </div>
            {/* <StatusButtons filterButtonDate={filterButtonDate} /> */}
          </div>
        </header>
        {/* Блок фильтров */}
        <div className="medFiltersContainer" style={{ padding: '20px 0px', borderBottom: 'none' }}>
          <div className="medFiltersRow">
            <div className="medFilterItem">
              <label>Начало периода</label>
              <input
                type="date"
                name="startTime"
                value={extendedFilters.startTime}
                onChange={handleExtendedFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="medFilterItem">
              <label>Конец периода</label>
              <input
                type="date"
                name="endTime"
                value={extendedFilters.endTime}
                onChange={handleExtendedFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="medFilterItem">
              <label>Автопарк</label>
              <input
                type="text"
                name="autoparkName"
                value={extendedFilters.autoparkName}
                onChange={handleExtendedFilterChange}
                placeholder="Название автопарка"
              />
            </div>
            <div className="medFilterItem">
              <label>Номер заявки</label>
              <input
                type="text"
                name="num"
                value={extendedFilters.num}
                onChange={handleExtendedFilterChange}
                placeholder="Номер заявки"
              />
            </div>
            <div className="medFilterItem">
              <button className="resetFiltersBtn" onClick={resetFilters}>
                Сбросить фильтры
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading__indicator">Загрузка...</div>
        ) : error ? (
          <div className="error__message">{error}</div>
        ) : (
          <>
            <TableContainer>
              <MedicList sortedAndSearchedPosts={(medLists as IMedicalCheck[]) || []} onUserClick={handleViewClick} />
            </TableContainer>

            {/* Компонент пагинации */}
            <div className="pagination-wrapper">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Компонент для текущей вкладки "Завершенные заявки на МО"
function CompletedMedicalRequestsTab({ user, onUserClick }: { user: any; onUserClick: (id: string) => void }) {
  const dispatch = useAppDispatch();
  const reduxMedLists = useAppSelector((state) => state.meddata.medicLists);
  const reduxTotalPages = useAppSelector((state) => state.meddata.totalPages);

  // Основной фильтр для Header
  const [filter, setFilter] = useState<{
    sort: string;
    query: string;
    filterQuery: string;
  }>({
    sort: '',
    query: '',
    filterQuery: 'fullName',
  });

  const [medStatus, setMedStatus] = useState('');

  // Дополнительные фильтры
  const [extendedFilters, setExtendedFilters] = useState({
    startTime: '',
    endTime: '',
    autoparkName: '',
    num: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Используем хук useBackendFilter для получения данных со старого эндпоинта
  const {
    data: medLists,
    loading,
    error,
    totalCount,
    totalPages,
    fetchData,
  } = useBackendFilter(`/medlist/medic/${user.id}`);

  // Подсчет статистики медицинских осмотров
  const medStats = useMemo(() => {
    const lists = (reduxMedLists as IMedicalCheck[]) || (medLists as IMedicalCheck[]) || [];
    const totalCount = lists.length || 0;
    const positiveCount = lists.filter((med) => med.status === 'completed').length || 0;
    const negativeCount = lists.filter((med) => med.status === 'rejected').length || 0;

    return {
      totalCount,
      positiveCount,
      negativeCount,
    };
  }, [reduxMedLists, medLists]);

  // Функция для сброса фильтров
  const resetFilters = () => {
    setFilter({
      sort: '',
      query: '',
      filterQuery: 'fullName',
    });
    setExtendedFilters({
      startTime: '',
      endTime: '',
      autoparkName: '',
      num: '',
    });
    setMedStatus('');
    setCurrentPage(1);
  };

  // Обработчик изменения расширенных фильтров
  const handleExtendedFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setExtendedFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Функция для получения данных с сервера с применением фильтров
  const applyFilters = () => {
    const filterParams: Record<string, any> = {
      page: currentPage,
      limit: itemsPerPage,
    };

    // Добавляем medicID если пользователь - медик
    if (user.medicProfile?.id) {
      filterParams.medicID = user.medicProfile.id;
    }

    // Добавляем поисковый запрос и поле поиска, если они есть
    if (filter.query && filter.filterQuery) {
      // Преобразование параметров поиска в соответствии с форматом бэкенда
      if (filter.filterQuery === 'name' || filter.filterQuery === 'middleName' || filter.filterQuery === 'firstName') {
        // Если выбрано имя или отчество, отправляем в поле firstName
        filterParams.firstName = filter.query;
      } else if (filter.filterQuery === 'lastName') {
        // Если выбрана фамилия, отправляем в поле lastName
        filterParams.lastName = filter.query;
      } else if (filter.filterQuery === 'fullName') {
        // Если выбрано ФИО, отправляем оригинальный параметр
        filterParams.fullName = filter.query;
      } else {
        // Для других параметров (date, birthDate) отправляем как есть
        filterParams[filter.filterQuery] = filter.query;
      }

      // Добавляем оригинальные параметры для совместимости
      filterParams.query = filter.query;
      filterParams.filterQuery = filter.filterQuery;
    }

    // Добавляем статус медосмотра, если он выбран
    if (medStatus) {
      filterParams.status = medStatus;
    }

    // Добавляем сортировку, если она выбрана
    if (filter.sort) {
      filterParams.sort = filter.sort;
    }

    // Добавляем период дат, если они указаны, конвертируя в нужный формат
    if (extendedFilters.startTime) {
      filterParams.startTime = extendedFilters.startTime;
    }
    if (extendedFilters.endTime) {
      filterParams.endTime = extendedFilters.endTime;
    }

    // Добавляем автопарк, если он указан
    if (extendedFilters.autoparkName) {
      filterParams.autoparkName = extendedFilters.autoparkName;
    }

    // Добавляем номер заявки, если он указан
    if (extendedFilters.num) {
      filterParams.num = extendedFilters.num;
    }

    fetchData(filterParams);
  };

  // Первоначальная загрузка данных
  useEffect(() => {
    applyFilters();
  }, [user.medicProfile?.id]);

  // Обновление данных при изменении фильтров или пагинации
  useEffect(() => {
    const handler = setTimeout(() => {
      applyFilters();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [
    filter.query,
    filter.filterQuery,
    filter.sort,
    extendedFilters.startTime,
    extendedFilters.endTime,
    extendedFilters.autoparkName,
    extendedFilters.num,
    medStatus,
    currentPage,
    itemsPerPage,
  ]);

  // Сохраняем данные в Redux при их получении
  useEffect(() => {
    if (medLists && !loading) {
      dispatch(
        setMedicLists({
          data: medLists,
          totalCount,
          totalPages,
        }),
      );
    }
  }, [medLists, loading, totalCount, totalPages, dispatch]);

  const handleStatusChange = (newValue: string) => {
    if (newValue !== medStatus) {
      setMedStatus(newValue);
      setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтра
    } else {
      setMedStatus('');
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const filterButtonDate = [
    {
      buttonLabel: 'Заключение',
      currentSelection: medStatus,
      handler: handleStatusChange,
      style: { width: '250px' },
      labelData: [
        {
          i_id: '1',
          i_name: 'role',
          i_type: 'checkbox',
          i_value: 'completed',
          i_label: 'Положительно',
        },
        {
          i_id: '2',
          i_name: 'role',
          i_type: 'checkbox',
          i_value: 'rejected',
          i_label: 'Отрицательно',
        },
        {
          i_id: '3',
          i_name: 'role',
          i_type: 'checkbox',
          i_value: 'waiting',
          i_label: 'Ждет контроля',
        },
      ],
    },
  ];

  const params = [
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'lastName',
      i_label: 'Фамилия',
    },
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'lastName',
      i_label: 'Имя',
    },
    {
      i_id: 'medlist',
      i_name: 'role',
      i_type: 'checkbox',
      i_value: 'middleName',
      i_label: 'Отчество',
    },
  ];

  return (
    <>
      <Header filter={filter} setFilter={setFilter} params={params} />
      <div className="admin__container">
        <header className="admin__header">
          <div className="admin__header-container">
            <h1>Завершенные заявки на медицинский осмотр</h1>
            <StatusButtons filterButtonDate={filterButtonDate} />
          </div>
          {/* Блок статистики медицинских осмотров */}
          <div className="">
            <div className="medStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
              Осмотров: {medStats.totalCount}, из них Положительных {medStats.positiveCount}, Отрицательных{' '}
              {medStats.negativeCount}
            </div>
          </div>
        </header>
        {/* Блок фильтров */}
        <div className="medFiltersContainer" style={{ padding: '20px 0px', borderBottom: 'none' }}>
          <div className="medFiltersRow">
            <div className="medFilterItem">
              <label>Начало периода</label>
              <input
                type="date"
                name="startTime"
                value={extendedFilters.startTime}
                onChange={handleExtendedFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="medFilterItem">
              <label>Конец периода</label>
              <input
                type="date"
                name="endTime"
                value={extendedFilters.endTime}
                onChange={handleExtendedFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="medFilterItem">
              <label>Автопарк</label>
              <input
                type="text"
                name="autoparkName"
                value={extendedFilters.autoparkName}
                onChange={handleExtendedFilterChange}
                placeholder="Название автопарка"
              />
            </div>
            <div className="medFilterItem">
              <label>Номер заявки</label>
              <input
                type="text"
                name="num"
                value={extendedFilters.num}
                onChange={handleExtendedFilterChange}
                placeholder="Номер заявки"
              />
            </div>
            <div className="medFilterItem">
              <button className="resetFiltersBtn" onClick={resetFilters}>
                Сбросить фильтры
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading__indicator">Загрузка...</div>
        ) : error ? (
          <div className="error__message">{error}</div>
        ) : (
          <>
            <TableContainer>
              <MedicList
                sortedAndSearchedPosts={(reduxMedLists as IMedicalCheck[]) || (medLists as IMedicalCheck[])}
                onUserClick={onUserClick}
              />
            </TableContainer>

            {/* Компонент пагинации */}
            <div className="pagination-wrapper">
              <Pagination
                currentPage={currentPage}
                totalPages={reduxTotalPages || totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Medic() {
  const user: any = useSelector((state: RootState) => state.user.user);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const handleUserClick = (id: string) => {
    setSelectedUserId(id);
  };

  // Конфигурация вкладок
  const tabsData = [
    {
      title: 'Заявки на МО',
      component: <MedicalRequestsTab user={user} onUserClick={handleUserClick} />,
    },
    {
      title: 'Завершенные заявки на МО',
      component: <CompletedMedicalRequestsTab user={user} onUserClick={handleUserClick} />,
    },
  ];

  return (
    <>
      {selectedUserId ? (
        <MedicDetails id={selectedUserId} onUserClick={handleUserClick} />
      ) : (
        <div className="tabs-wrapper">
          <Tabs tabsData={tabsData} />
        </div>
      )}
    </>
  );
}

export default Medic;
